import { ref, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { functionalActivityApi, type FunctionalActivity } from '@/entities/FunctionalActivity';
import { toastError, toastSuccess, useCustomToast } from '@/shared/lib';
import CloneFunctionalActivityPermissionsModal from '../ui/functional-activities/CloneFunctionalActivityPermissionsModal.vue';

interface UseFunctionalActivityCloneParams {
  activities: Ref<FunctionalActivity[]>;
}

export function useFunctionalActivityClone({ activities }: UseFunctionalActivityCloneParams) {
  const toast = useCustomToast();
  const cloneTargetActivity = ref<FunctionalActivity | null>(null);
  const isCloneSaving = ref(false);

  async function clonePermissions(
    targetId: number,
    sourceId: number
  ): Promise<{ success: boolean }> {
    try {
      await functionalActivityApi.clonePermissions(targetId, sourceId);
      toastSuccess(toast, 'Permissões clonadas com sucesso');
      return { success: true };
    } catch {
      toastError(toast, 'Erro ao clonar permissões, tente novamente.');
      return { success: false };
    }
  }

  const cloneModal = useModal({
    component: CloneFunctionalActivityPermissionsModal,
    attrs: {
      title: 'Clonar Permissões',
      get functionalActivity() {
        return cloneTargetActivity.value;
      },
      get isSaving() {
        return isCloneSaving.value;
      },
      onClose() {
        cloneModal.close();
      },
      async onSave(data: { sourceId: number }) {
        if (!cloneTargetActivity.value) return;
        isCloneSaving.value = true;
        try {
          const result = await clonePermissions(cloneTargetActivity.value.id, data.sourceId);
          if (result.success) {
            cloneModal.close();
          }
        } finally {
          isCloneSaving.value = false;
        }
      },
    },
  });

  function handleClone(id: number) {
    const activity = activities.value.find((item) => item.id === id);
    if (!activity) return;
    cloneTargetActivity.value = activity;
    cloneModal.open();
  }

  return { clonePermissions, handleClone };
}
